import { useEffect, useState } from 'react';
import ObjectDetailsPanel from '../../../views/instances/tabs/ObjectBrowser/ObjectDetailsPanel';

// Third-parties.
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { TreeTable, TreeTableExpandedKeysType,
	TreeTableSelectionKeys } from 'primereact/treetable';

// Types.
import ExecutionPlan from '../../../types/instance/ExecutionPlan';

// Components
import GenericPlan from './GenericPlan';
import KnowledgeBasePlanButton from "../../buttons/KnowledgeBasePlanButton";
import IndexRecommendations from "./IndexRecommendations";

// Helpers.
import { Step, formatNumber, getStepDetails } from '../../../helpers/utils';
import { useParams } from "react-router-dom";

export default function SybasePlan (props: { executionPlan: ExecutionPlan, hasObjectStatistics: boolean })
{
	const [treeSteps, setTreeSteps] = useState<Step[]> ([]);
	const [expandedKeys, setExpandedKeys] =
		useState<TreeTableExpandedKeysType> ({});
	const [selectedStepKey, setSelectedStepKey] =
		useState<TreeTableSelectionKeys> (null);
	const [stepDetails, setStepDetails] = useState<any[]> ([]);
	const [stepOperation, setStepOperation] = useState<string> ('');
	const [error, setError] = useState<string | null>(null);
	const [panelState, setPanelState] = useState<{ open: boolean; object: any }>({ open: false, object: null });
	const { instanceId } = useParams();

	function getStepOperation (details: any[]): string
	{
		const detail: any[] =
			details.filter (detail => detail.name === 'operation');
		return detail.length === 1 ? detail[0].value : '';
	}

	useEffect (() =>
	{
		// These closure variables are required by the recursive functions.
		let n: number = 0;
		const expanded: TreeTableExpandedKeysType = {};

		function getKey (): string
		{
			return String (++n);
		}

		function logMissedField (field: string, type: string): void
		{
			console.log ('Missed ' + field + ': ' + type);
		}

		function getData (step: any, operation: string, costField: string): any
		{
			const data: any = { operation: operation };

			for (const field in step)
			{
				const type: string = typeof (step[field]);

				if (type === 'string' || type === 'boolean')
				{
					data[field] = String (step[field]);
				}
				else if (type === 'number')
				{
					data[field] = formatNumber (step[field]);
				}
				else if (type === 'object')
				{
					if (field === 'est')
					{
						data['cost'] = step[field][costField];
					}
				}
				else
				{
					logMissedField (field, type);
				}
			}

			return data;
		}

		function getChildren (step: any): Step[]
		{
			const children: Step[] = [];

			for (const field in step)
			{
				const type: string = typeof (step[field]);

				if (type === 'object' && field !== 'act' && field !== 'est')
				{
					children.push (getStep (step[field], field, 'lio'));
				}
			}

			return children;
		}

		function getStep (step: any, operation: string, costField: string): Step
		{
			const key: string = getKey ();

			// Expand all rows as a side-effect.
			expanded[key] = true;

			const treeStep: Step =
			{
				key: key,
				data: getData (step, operation, costField),
				children: getChildren (step)
			};

			return treeStep;
		}

		try
		{
			const plan: any = JSON.parse (props.executionPlan.sqlplan);
			// console.log ('plan', plan);
			const tree: Step[] = [];

			// Multiple near-identical plans may be returned. Use the first one.
			let queryPlan: any = plan.query.plan;

			if (Array.isArray (queryPlan))
			{
				queryPlan = queryPlan[0];
			}

			tree.push (getStep (queryPlan.opTree, 'opTree', 'totalLio'));
			const details: any[] = getStepDetails (tree, '1');
			setTreeSteps (tree);
			setExpandedKeys (expanded);
			setSelectedStepKey ('1');
			setStepDetails (getStepDetails (tree, '1'));
			setStepOperation (getStepOperation (details));
		}
		catch (x: any)
		{
			setTreeSteps ([]);
			setExpandedKeys ({});
			setSelectedStepKey (null);
			setStepDetails ([]);
			setStepOperation ('');
			setError (String (x));
		}
	}, [props.executionPlan.sqlplan]);

	useEffect (() =>
	{
		const details: any[] = getStepDetails (treeSteps, selectedStepKey);
		setStepDetails (getStepDetails (treeSteps, selectedStepKey));
		setStepOperation (getStepOperation (details));
	}, [treeSteps, selectedStepKey]);

	// The bracket must be present and on the same line as the return.
	if (error)
	{
		return (<>
			<GenericPlan executionPlan={props.executionPlan} />
			<IndexRecommendations
				indexRecommendations={props.executionPlan.indexrecommendations}
			/>
		</>)
	}

	return (
		<>
			<Splitter>
				<SplitterPanel size={65} className="plan-master-panel">
					<TreeTable
						value={treeSteps}
						expandedKeys={expandedKeys}
						onToggle={e => setExpandedKeys (e.value)}
						selectionMode="single"
						resizableColumns
						reorderableColumns
						selectionKeys={selectedStepKey}
						onSelectionChange={e => setSelectedStepKey (e.value)}
						className="treetable-responsive"
					>
						<Column header="Operation" field="operation" expander
							className="col-width-35 plan-first-column"/>
						<Column header="Table" field="objName"
							className="col-width-35"/>
						<Column header="Cost" field="cost"
							className="col-width-30 text-end"/>
					</TreeTable>
				</SplitterPanel>
				<SplitterPanel size={35} className="plan-detail-panel">
					<DataTable value={stepDetails} resizableColumns stripedRows>
						<Column header="Name" field="name"
							className="col-width-50 plan-first-column"/>
						<Column
							header="Value"
							field="value"
							className="col-width-50"
							body={(rowData: any) => {
								// If this is the objName, render as a link
								if (rowData.name === "objName" && rowData.value && props.hasObjectStatistics) {
									return (
										<button
											type="button"
											className="btn btn-link p-0"
											style={{ textDecoration: "underline", color: "#007bff", cursor: "pointer" }}
											onClick={() => setPanelState({
												open: true,object: {
													...(rowData.name === "Index Name"
															? { indexname: rowData.value }
															: { tablename: rowData.value }),
													objectType: rowData.name === "Index Name" ? "Index" : "Table",
													instanceId: Number(instanceId)
												}
											})}
										>
											{rowData.value}
										</button>
									);
								}
								return rowData.value;
							}}
						/>
					</DataTable>
					<KnowledgeBasePlanButton databaseType="sybase"
						planOperation={stepOperation}
						planOption=""/>
				</SplitterPanel>
			</Splitter>
			<IndexRecommendations
				indexRecommendations={props.executionPlan.indexrecommendations}
			/>
			<ObjectDetailsPanel
				object={panelState.object}
				open={panelState.open}
				onClose={() => setPanelState({ open: false, object: null })}
			/>
		</>
	);
}
